import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  // Org MFA enforcement: {mfa_required, mfa_enabled, must_setup}
  const [mfaEnforcement, setMfaEnforcement] = useState(null);
  const initialFetchDone = useRef(false);

  // Logout function (defined early to avoid circular deps)
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setMfaEnforcement(null);
  }, []);

  // Check org MFA enforcement after user is loaded
  const checkMfaEnforcement = useCallback(async (storedToken) => {
    try {
      const res = await axios.get(`${API_URL}/api/mfa/org/enforcement-status`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      setMfaEnforcement(res.data);
    } catch {
      setMfaEnforcement({ mfa_required: false, mfa_enabled: false, must_setup: false });
    }
  }, []);

  // Fetch user on initial load only
  useEffect(() => {
    const fetchUser = async () => {
      const storedToken = localStorage.getItem('token');
      if (!storedToken) {
        setLoading(false);
        return;
      }
      
      if (initialFetchDone.current) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` }
        });
        setUser(response.data);
        initialFetchDone.current = true;
        await checkMfaEnforcement(storedToken);
      } catch (error) {
        console.error('Failed to fetch user:', error);
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUser();
  }, []); // Only run once on mount

  const login = async (email, password) => {
    const hostname = window.location.hostname;
    const isMainApp = hostname === 'app.getassura.app' || hostname === 'app-staging.getassura.app' || hostname === 'localhost';
    const isLocalhostSubdomain = hostname.endsWith('.localhost');
    const isStagingSubdomain = hostname.endsWith('.app-staging.getassura.app');
    const isProductionSubdomain = hostname.endsWith('.getassura.app') && !isMainApp && !isStagingSubdomain;
    let subdomain = null;
    if (isLocalhostSubdomain) {
      subdomain = hostname.replace('.localhost', '').replace(/-/g, '');
    } else if (isStagingSubdomain) {
      subdomain = hostname.replace('.app-staging.getassura.app', '').replace(/-/g, '');
    } else if (isProductionSubdomain) {
      subdomain = hostname.replace('.getassura.app', '').replace(/-/g, '');
    }
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password, subdomain });
    const data = response.data;

    // MFA required — return all MFA flags to the caller (LoginPage handles redirect)
    if (data.mfa_required) {
      return {
        mfa_required: true,
        mfa_enforced: data.mfa_enforced || false,
        mfa_token: data.mfa_token,
        message: data.message
      };
    }

    const { access_token, user: userData } = data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    initialFetchDone.current = true;
    await checkMfaEnforcement(access_token);
    return userData;
  };

  // Called from MFAVerifyPage after successful TOTP verification
  const loginWithToken = async (access_token, userData) => {
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    initialFetchDone.current = true;
    await checkMfaEnforcement(access_token);
  };

  // Called by SecurityPage after MFA setup is completed
  const refreshMfaEnforcement = useCallback(async () => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) await checkMfaEnforcement(storedToken);
  }, [checkMfaEnforcement]);

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(newUser);
    initialFetchDone.current = true;
    return newUser;
  };

  // RBAC Role Checks (Sprint 1 spec section 3)
  const isDPO = user?.role === 'dpo' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';
  const isClientAdmin = user?.role === 'client_admin';
  const isEntityAdmin = user?.role === 'entity_admin';
  const isClientViewer = user?.role === 'client_viewer' || user?.role === 'client_view';
  const isEntityViewer = user?.role === 'entity_viewer' || user?.role === 'entity_view';
  const isViewer = isClientViewer || isEntityViewer;
  const isReadOnly = isViewer;  // Viewers are read-only

  // Super Admin is a PLATFORM operator, not a DATA operator (per client spec).
  // Read-only on every compliance / regulatory module: ROPA, DPIA, AI, LIA, TIA,
  // Risk Register, Breach, DSR, Tasks, Documents, Vendor, Maturity, Questionnaire,
  // Readiness, Pre-screen, Dataflow, Audit Logs, Dashboard.
  // Use this flag (NOT `isReadOnly`) on every compliance-page write affordance.
  // Platform-config pages (clients, users, subscriptions, modules, MFA policy) keep
  // using `isReadOnly` so super_admin retains full CRUD there.
  const isComplianceReadOnly = isReadOnly || isSuperAdmin;

  const value = {
    user, token, loading,
    login, register, loginWithToken, logout,
    mfaEnforcement, refreshMfaEnforcement,
    isAuthenticated: !!user && !!token,
    // Role checks (for UI conditional rendering)
    isDPO,
    isSuperAdmin,
    isClientAdmin,
    isEntityAdmin,
    isClientViewer,
    isEntityViewer,
    isViewer,
    isReadOnly,
    isComplianceReadOnly,
    // Legacy checks (kept for backward compatibility)
    isOrgAdmin: isDPO  // Legacy: map to isDPO
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
